/**
 * Piece 2 (Phase B) — plan-hero batch-draft affordance.
 *
 * Two states in one card:
 *   1. IDLE     → the "Draft the next 4 weeks →" CTA + the MUST-EXPLAIN copy
 *                 inline. Rendered only when the caller has already decided the
 *                 gate holds (flag ON + ≥2 undrafted in-arc weeks) — this
 *                 component does NOT re-check the flag.
 *   2. RESULT   → after a draft runs: a one-line summary ("Weeks 5 to 8 are ready.")
 *                 + the server's `explain` line + any cross-week warnings (listed;
 *                 a `blockedSpike` is boxed and surfaced prominently) + a dismiss.
 *                 `noop`/`needs_review`/network-error each get a plain message.
 *
 * Presentation only: the parent owns the fetch (useBatchDraft) and the refresh
 * of the macro/drafted data after a saved/noop result.
 */

import { useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { BatchDraftResult } from '../../lib/engineDraft';
import { batchDraftSaved } from '../../lib/engineDraft';
import { originForTrainPath } from '../../lib/draftWeek';
import styles from './BatchDraftCard.module.css';

/** The MUST-EXPLAIN placeholder copy (founder will tweak; server sends its own
 * `explain` verbatim on a saved result). */
export const BATCH_DRAFT_EXPLAIN =
  "We build your detailed workouts about 4 weeks at a time so they stay matched to how you're actually training.";

export interface BatchDraftCardProps {
  /** SP-6 — the EXACT week numbers this draft will author (the first ≤4 undrafted
   *  in-arc weeks from the current week, ascending). The label names them so it
   *  can't claim "the next 4 weeks" while actually drafting far-future gaps. */
  targetWeeks: number[];
  drafting: boolean;
  errored: boolean;
  result: BatchDraftResult | null;
  onDraft: () => void;
  onDismiss: () => void;
  /** B-3 — when `draft_week_journey_v1` is ON, the card is a pure navigation CTA
   *  into the /v2/plan/draft review takeover: no in-place draft, the RESULT state
   *  retires (results land on the review card), and a trust line sits under the
   *  CTA. Default false = BYTE-IDENTICAL in-place batch draft + RESULT state. */
  draftJourney?: boolean;
  /**
   * R-Train-1 (CD, ratified 2026-09-09) — `'rail'` is the desktop shell's
   * right-rail form: the same card drawn as a rail card (paper, 1px
   * `--ink-200`, radius 14, padding 18) with a "Draft ahead" kicker, a body
   * that NAMES the reserved weeks, and a full-width 38px marine pill. At
   * ≥1024px the rail is the card's only home; below 1024 the page keeps
   * `'page'`, byte-identical to what it has always rendered.
   *
   * Wave 2 (spec-2.0-build-wave2.md 7.11, TDR-2) — `'v2'` is the 2.0 Train Draft
   * card (`train_2_0_v1`): ONE shape for every place it is mounted (This week, the
   * season page's phase group, the desktop rail), title "Draft weeks 7 to 10", the
   * line `DRAFT_CARD_LINE` and the button "Draft". It is the journey card, so it
   * needs `draftJourney`; without it (`draft_week_journey_v1` off, the sentence
   * would not be true) `'v2'` takes the `'page'` render path, the legacy card.
   */
  variant?: 'page' | 'rail' | 'v2';
  /**
   * FIX-TRAIN — the `from=` value the journey CTA of the `'page'` and `'rail'` cards sends to the review, which is
   * where its back link and "Not now" return. Default `plan`, the legacy origin, byte for byte. The calendar rail
   * passes `season` with `train_2_0_v1` on, because there `plan` is This week and the review's own label for it
   * reads "Season plan". The `'v2'` card needs none: it reads the page it is mounted on.
   */
  origin?: string;
}

/**
 * SP-6 / R-Train-1 — the EXACT weeks a batch will author: the first ≤4
 * undrafted weeks from the current week forward (inclusive — the current week
 * may itself be undrafted), ascending. Mirrors the server's
 * `selectUndraftedWeeks` / MAX_BATCH=4.
 *
 * ONE implementation, because two surfaces ask the question now: `/plan` (from
 * `useMacroPlan` + `useDraftedWeeks`) and `/plan/calendar` (from the folded
 * `buildSeasonCalendar` rows). A second copy is how the two would come to
 * disagree about which weeks the CTA names.
 *
 * `[]` when the current week is unknown — which is also the "no CTA yet"
 * answer, since the gate is `length >= 2`.
 */
export function draftTargetWeeks(
  weekNumbers: number[],
  currentWeek: number | null,
  draftedWeeks: Set<number>,
): number[] {
  if (currentWeek === null) return [];
  return weekNumbers
    .filter((n) => n >= currentWeek && !draftedWeeks.has(n))
    .sort((a, b) => a - b)
    .slice(0, 4);
}

/** SP-6 — name the weeks a batch will draft: "week 6" / "weeks 6 to 8"
 *  (contiguous) / "weeks 4, 14 to 16" (compacted runs when the undrafted weeks
 *  aren't adjacent). R-Train-1 register: ranges read "6 to 8", never an en dash. */
export function formatDraftWeeksLabel(weeks: number[]): string {

  const nums = [...new Set(weeks.filter((n) => Number.isInteger(n)))].sort((a, b) => a - b);
  if (nums.length === 0) return 'your next weeks';
  const runs: string[] = [];
  let start = nums[0]!;
  let prev = nums[0]!;
  for (let i = 1; i <= nums.length; i++) {
    const n = nums[i];
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    runs.push(start === prev ? `${start}` : `${start} to ${prev}`);
    if (n !== undefined) {
      start = n;
      prev = n;
    }
  }
  const noun = nums.length === 1 ? 'week' : 'weeks';
  return `${noun} ${runs.join(', ')}`;
}

/** The line under the v2 card's title: wave 1 string 56, the same words Today's Next steps row prints. */
export const DRAFT_CARD_LINE = 'You draft them, then review each week before anything is added.';

/** "weeks 6 to 8" → "Weeks 6 to 8" for the rail body's sentence opener. */
function sentenceCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/** "Weeks 5 to 8 are ready." / "Week 5 is ready." from the saved week list.
 *  Register (critic C4): the range reads "to", never an en dash. */
function readySummary(weeks: Array<{ weekNumber: number }>): string {
  const nums = weeks.map((w) => w.weekNumber).sort((a, b) => a - b);
  if (nums.length === 0) return 'Your next weeks are ready.';
  if (nums.length === 1) return `Week ${nums[0]} is ready.`;
  return `Weeks ${nums[0]} to ${nums[nums.length - 1]} are ready.`;
}

/** Athlete-friendly heads-up for a cross-week ramp. The raw validator message
 * ("Week 8 load jumps 95.92% from the prior week (cap 10%)") is too clinical and
 * alarming for the athlete surface: convey the shape, not the raw number.
 * VERBATIM the same sentence `lib/draftWeek.ts` speaks on the draft surface. */
function warningLine(w: { weekNumber: number }): string {
  return `Week ${w.weekNumber} builds up faster than usual from the week before. Ease into its harder days.`;
}

/**
 * The 2.0 Draft card (`variant="v2"`, TDR-2). Title and line are the words of Today's Next steps row for the same
 * weeks (the title is `Draft ` plus `formatDraftWeeksLabel`, no arrow); the button is "Draft" and goes to the
 * review with the origin the card is mounted on (`originForTrainPath`): the season page sends `season`, a week page
 * sends `week-N`, This week sends `thisweek` (the review's back link and "Not now" return to that same place).
 */
function DraftCardV2({ targetWeeks }: { targetWeeks: number[] }) {
  const titleId = useId();
  const { pathname } = useLocation();
  const from = originForTrainPath(pathname);
  const weeksParam = targetWeeks.slice().sort((a, b) => a - b).join(',');
  return (
    <section className={styles.v2Card} role="region" aria-label="Draft upcoming weeks">
      <p id={titleId} className={styles.v2Title}>{`Draft ${formatDraftWeeksLabel(targetWeeks)}`}</p>
      <p className={styles.v2Line}>{DRAFT_CARD_LINE}</p>
      <Link to={`/plan/draft?weeks=${weeksParam}&from=${from}`} className={styles.v2Cta} aria-describedby={titleId}>
        Draft
      </Link>
    </section>
  );
}

export function BatchDraftCard({
  targetWeeks,
  drafting,
  errored,
  result,
  onDraft,
  onDismiss,
  draftJourney = false,
  variant = 'page',
  origin = 'plan',
}: BatchDraftCardProps) {
  // ── 2.0 Train — the one v2 shape, for the page and the rail alike. Only with the journey on.
  if (variant === 'v2' && draftJourney) return <DraftCardV2 targetWeeks={targetWeeks} />;

  // ── R-Train-1 — the rail form (desktop shell ≥1024, /plan + /plan/calendar).
  //    Same card, same states, same gate: only the chrome and the copy change,
  //    because a 300px rail cannot carry the page card's 44px CTA and its
  //    MUST-EXPLAIN paragraph. The body NAMES the weeks the CTA would draft, so
  //    the rail can never claim more than the button does.
  if (variant === 'rail') {
    if (result || errored) {
      return (
        <section className={styles.railCard} role="region" aria-label="Batch draft result">
          {renderResultBody(result, errored)}
          <button type="button" className={styles.dismiss} onClick={onDismiss}>
            Dismiss
          </button>
        </section>
      );
    }

    const weeksLabel = formatDraftWeeksLabel(targetWeeks);
    const ctaLabel = `Draft ${weeksLabel}`;
    // Agreement follows the LABEL, not the array: `formatDraftWeeksLabel`
    // dedups, so [6, 6] is "week 6" and must read "is".
    const reserved = `${sentenceCase(weeksLabel)} ${
      new Set(targetWeeks).size === 1 ? 'is' : 'are'
    } reserved but not drafted yet.`;

    return (
      <section className={styles.railCard} role="region" aria-label="Draft upcoming weeks">
        <p className={styles.railKicker}>Draft ahead</p>
        <p className={styles.railBody}>{reserved}</p>
        {draftJourney ? (
          <>
            <Link
              to={`/plan/draft?weeks=${targetWeeks
                .slice()
                .sort((a, b) => a - b)
                .join(',')}&from=${origin}`}
              className={styles.railCta}
            >
              {ctaLabel}
            </Link>
            <p className={styles.trustLine}>You&rsquo;ll review them before anything is added.</p>
          </>
        ) : (
          <button
            type="button"
            className={styles.railCta}
            onClick={onDraft}
            disabled={drafting}
          >
            {drafting ? 'Drafting…' : ctaLabel}
          </button>
        )}
      </section>
    );
  }

  // ── B-3 journey — pure navigation CTA into the review takeover ─────────
  if (draftJourney) {
    const weeksParam = targetWeeks.slice().sort((a, b) => a - b).join(',');
    return (
      <section className={styles.card} role="region" aria-label="Draft upcoming weeks">
        <p className={styles.eyebrow}>Keep your plan matched</p>
        <p className={styles.explain}>{BATCH_DRAFT_EXPLAIN}</p>
        <div className={styles.actions}>
          <Link
            to={`/plan/draft?weeks=${weeksParam}&from=${origin}`}
            className={styles.cta}
          >
            {`Draft ${formatDraftWeeksLabel(targetWeeks)} →`}
          </Link>
        </div>
        <p className={styles.trustLine}>You&rsquo;ll review them before anything is added.</p>
      </section>
    );
  }

  // ── RESULT state ──────────────────────────────────────────────────────
  if (result || errored) {
    return (
      <section className={styles.card} role="region" aria-label="Batch draft result">
        {renderResultBody(result, errored)}
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          Dismiss
        </button>
      </section>
    );
  }

  // ── IDLE state — the CTA ──────────────────────────────────────────────
  return (
    <section className={styles.card} role="region" aria-label="Draft upcoming weeks">
      <p className={styles.eyebrow}>Keep your plan matched</p>
      <p className={styles.explain}>{BATCH_DRAFT_EXPLAIN}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.cta} onClick={onDraft} disabled={drafting}>
          {drafting ? 'Drafting…' : `Draft ${formatDraftWeeksLabel(targetWeeks)} →`}
        </button>
      </div>
    </section>
  );
}

function renderResultBody(result: BatchDraftResult | null, errored: boolean) {
  if (errored || !result) {
    return (
      <p className={styles.errorText}>
        We couldn’t draft your next weeks just now. Please try again in a moment.
      </p>
    );
  }

  if (result.state === 'noop') {
    return <p className={styles.resultSummary}>Your upcoming weeks are already drafted.</p>;
  }

  if (!batchDraftSaved(result)) {
    // Non-saved outcomes are DETERMINISTIC (validation / availability), not
    // transient — "try again in a moment" would send the athlete in circles.
    if (result.state === 'disabled') {
      return (
        <p className={styles.resultSummary}>
          Drafting several weeks at once isn’t available right now.
        </p>
      );
    }
    // needs_review (nothing drafted — e.g. the first week failed validation).
    return (
      <p className={styles.resultSummary}>
        We couldn’t build those weeks
        {typeof result.failedWeek === 'number'
          ? `. Week ${result.failedWeek} needs another look`
          : '. They need another look'}
        . Your current plan is unchanged.
      </p>
    );
  }

  const savedWeeks = result.weeks ?? [];
  const warnings = result.warnings ?? [];
  const blockedSpike = result.blockedSpike === true;
  const partial = result.partial === true;

  return (
    <>
      <p className={styles.resultSummary}>{readySummary(savedWeeks)}</p>
      {partial && typeof result.failedWeek === 'number' && (
        <p className={styles.resultExplain}>
          Week {result.failedWeek} needs another look and wasn’t drafted. The weeks before it are
          ready.
        </p>
      )}
      {result.explain && <p className={styles.resultExplain}>{result.explain}</p>}
      {warnings.length > 0 &&
        (blockedSpike ? (
          <div className={styles.warningsAlert}>
            <p className={styles.warningsAlertTitle}>A note on your build</p>
            <ul className={styles.warnings}>
              {warnings.map((w, i) => (
                <li key={`${w.weekNumber}-${i}`} className={styles.warning}>
                  {warningLine(w)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <ul className={styles.warnings}>
            {warnings.map((w, i) => (
              <li key={`${w.weekNumber}-${i}`} className={styles.warning}>
                {warningLine(w)}
              </li>
            ))}
          </ul>
        ))}
    </>
  );
}
